import React, { useEffect, useRef, useState } from 'react';
import { Modal, InputNumber } from 'antd';
import { Bounce, toast } from 'react-toastify';
import useAuthRequest from '../../../services/requests.service';

const AddSeatsModal = ({ plan, onClose, onSuccess }) => {
  const request = useAuthRequest();
  const [newQty, setNewQty] = useState(plan.max_account + 1);
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const debounceRef = useRef(null);

  const added = newQty - plan.max_account;

  useEffect(() => {
    if (newQty <= plan.max_account) { setPreview(null); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsPreviewLoading(true);
      try {
        const res = await request.getDataAPI(
          `stripe/previewinvoice/${plan.stripeCustomerId}?subscriptionId=${plan.stripeSubscriptionId}&qty=${newQty}`
        );
        setPreview(res.amountDue);
      } catch (_) {
        setPreview(null);
      } finally {
        setIsPreviewLoading(false);
      }
    }, 600);
    return () => clearTimeout(debounceRef.current);
  }, [newQty]);

  const handleSubmit = async () => {
    setIsLoading(true);
    if (!plan.stripeSubscriptionId) {
      toast.error("Identifiant d'abonnement manquant", {
        position: 'top-right', autoClose: 4000, theme: 'light', transition: Bounce,
      });
      setIsLoading(false);
      return;
    }
    try {
      await request.putDataAPI(`stripe/subscription/${plan.stripeSubscriptionId}`, { newQty });
      toast.success('Sièges mis à jour avec succès', {
        position: 'top-right', autoClose: 4000, theme: 'light', transition: Bounce,
      });
      onSuccess();
    } catch (_) {
      toast.error('Erreur lors de la mise à jour des sièges', {
        position: 'top-right', autoClose: 4000, theme: 'light', transition: Bounce,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={true}
      title="Ajouter des sièges"
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Confirmer"
      confirmLoading={isLoading}
      okButtonProps={{ disabled: newQty <= plan.max_account }}
      centered
    >
      <div className="flex flex-col gap-4 py-2">
        <div className="text-sm text-slate-500">
          Sièges actuels : <strong>{plan.max_account}</strong> · Actifs : <strong>{plan.activeCount}</strong>
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-600 mb-2">Nouvelle quantité totale</div>
          <InputNumber
            min={plan.max_account + 1}
            value={newQty}
            onChange={(v) => setNewQty(v ?? plan.max_account + 1)}
            className="w-full"
          />
          {added > 0 && (
            <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="text-xs text-slate-500 mb-1">+{added} siège{added > 1 ? 's' : ''}</div>
              {isPreviewLoading ? (
                <div className="text-xs text-slate-400">Calcul du prorata...</div>
              ) : preview !== null ? (
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">Prorata facturé maintenant</span>
                  <span className="text-sm font-bold text-slate-800">{(preview / 100).toFixed(2)}€</span>
                </div>
              ) : (
                <div className="text-xs text-slate-400">Le prorata sera calculé par Stripe</div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddSeatsModal;
